import type { ArenaCryptoDepositProvider, ArenaDepositMethods, ArenaFiatDepositProvider } from "@cede/types";

export type ArenaDepositProviderSettings = {
  /**
   * Unifold serves both channels: crypto for everyone, and fiat for the listed countries while
   * Crossmint is on, for everyone while it is off.
   */
  unifoldEnabled: boolean;
  crossmintEnabled: boolean;
  /** The legacy on-chain USDT transfer, which serves crypto while Unifold is off. */
  onchainEnabled: boolean;
  /** Countries whose FIAT goes to Unifold while both card rails are on (uppercase ISO alpha-2). */
  unifoldCountries: readonly string[];
};

/** Parses the `deposit_unifold_countries` CSV flag: trims, uppercases, drops blanks. */
export function parseArenaDepositCountries(csv: string): string[] {
  return [
    ...new Set(
      csv
        .split(",")
        .map((code) => code.trim().toUpperCase())
        .filter((code) => code.length > 0),
    ),
  ];
}

/**
 * Crossmint unless the country is on the Unifold list; null when neither rail is on.
 * An unresolved country takes the default (Crossmint).
 */
function resolveFiat(
  { unifoldEnabled, crossmintEnabled, unifoldCountries }: ArenaDepositProviderSettings,
  countryCode: string | null,
): ArenaFiatDepositProvider | null {
  if (!crossmintEnabled) return unifoldEnabled ? "unifold" : null;
  if (!unifoldEnabled) return "crossmint";
  if (countryCode != null && unifoldCountries.includes(countryCode.toUpperCase())) return "unifold";
  return "crossmint";
}

/** Unifold's wallet path, else the on-chain USDT transfer; null when both are off. */
function resolveCrypto({
  unifoldEnabled,
  onchainEnabled,
}: ArenaDepositProviderSettings): ArenaCryptoDepositProvider | null {
  if (unifoldEnabled) return "unifold";
  return onchainEnabled ? "onchain" : null;
}

/** The deposit channels open to a visitor from `countryCode`, each with the provider serving it. */
export function resolveArenaDepositMethods(
  settings: ArenaDepositProviderSettings,
  countryCode: string | null,
): ArenaDepositMethods {
  return { fiat: resolveFiat(settings, countryCode), crypto: resolveCrypto(settings) };
}
